import { z } from "zod";
import { ImportAllergySchema, ImportFamilyHistoryRowSchema, ImportImmunizationSchema, ImportLabAccessionSchema, ImportLegacyVisitSchema, ImportMedicationSchema, ImportPmhSchema, ImportSocialHistorySchema, } from "./patient-import.js";
import { IdSchema, IsoDateTimeSchema } from "./primitives.js";
/**
 * Importing a new patient from another EMR's chart page (EXECDEV-227).
 *
 * BobDoc reads a chart page the physician has open in another EMR, entirely in
 * the browser. The physician reviews what it found and registers the patient
 * through the ordinary registration or walk-in writer; this is the save that
 * follows: the chart entries they kept, in the chart importer's own row shapes,
 * for the patient just registered. The server writes them through
 * api.import_patient in a run whose source system is `emr-page:<source>`, so
 * every row reads as imported and names the EMR it came from.
 *
 * The page itself never travels. Source references are positional
 * (`medication:0`), never a drug or a name, because the run ledger keeps them.
 */
export const EmrPageSourceSchema = z.enum(["telus-chr"]);
/** How each supported EMR is named to a clinician and in the run ledger. */
export const EMR_PAGE_SOURCE_LABELS = {
    "telus-chr": "TELUS CHR",
};
/** The run ledger's source system for a page import: `emr-page:<source>`. */
export function emrPageImportSourceSystem(source) {
    return `emr-page:${source}`;
}
export const EmrPageChartImportInputSchema = z
    .object({
    source: EmrPageSourceSchema,
    allergies: z.array(ImportAllergySchema).max(200).optional(),
    medications: z.array(ImportMedicationSchema).max(200).optional(),
    pmh: z.array(ImportPmhSchema).max(200).optional(),
    familyHistory: z.array(ImportFamilyHistoryRowSchema).max(200).optional(),
    socialHistory: z.array(ImportSocialHistorySchema).max(200).optional(),
    immunizations: z.array(ImportImmunizationSchema).max(200).optional(),
    labs: z
        .object({
        labName: z.string().trim().min(1).max(160),
        accessions: z.array(ImportLabAccessionSchema).min(1).max(200),
    })
        .strict()
        .optional(),
    /** Past visits and their notes, saved as legacy visits: never encounters here. */
    visits: z.array(ImportLegacyVisitSchema).max(500).optional(),
})
    .strict();
export const EmrPageChartImportRequestSchema = z
    .object({
    siteId: IdSchema,
    /**
     * One key per reviewed page. A retry after a failure or a lost response
     * reuses it: the run resumes and entries already written are left alone.
     * Kept short enough to carry the source system in the run's own key.
     */
    idempotencyKey: z.string().min(8).max(96),
    input: EmrPageChartImportInputSchema,
})
    .strict();
const ImportCountSchema = z.number().int().nonnegative();
export const EmrPageChartImportReceiptSchema = z
    .object({
    patientId: IdSchema,
    /** True when this key's import had already finished: nothing was written again. */
    alreadySaved: z.boolean(),
    counts: z
        .object({
        created: ImportCountSchema,
        linked: ImportCountSchema,
        unchanged: ImportCountSchema,
        skipped: ImportCountSchema,
        conflict: ImportCountSchema,
    })
        .strict(),
    /** Entries the chart did not take, by section and ledger key; never their content. */
    conflicts: z
        .array(z
        .object({
        section: z.enum(["profile", "labs", "visits"]),
        itemKey: z.string().min(1).max(240),
        code: z.string().min(1).max(80),
    })
        .strict())
        .max(1000),
})
    .strict();
/**
 * Whether a clinic offers registering patients from another EMR's chart page.
 * Off until a clinic administrator turns it on, and only ever offered by local
 * development and hosted staging builds. While it is off no chart page import
 * can start (`api.emr_page_import_setting`).
 */
export const EmrPageImportSettingSchema = z
    .object({
    enabled: z.boolean(),
    /** 0 until an administrator first changes it. */
    version: z.number().int().nonnegative(),
    updatedAt: IsoDateTimeSchema.nullable(),
    /** Whether the one asking is a clinic administrator, who may change it. */
    canChange: z.boolean(),
})
    .strict();
/** A clinic administrator turning the import on or off, from the version they saw. */
export const SetEmrPageImportSettingRequestSchema = z
    .object({
    siteId: IdSchema,
    enabled: z.boolean(),
    expectedVersion: z.number().int().nonnegative(),
})
    .strict();
//# sourceMappingURL=emr-page-import.js.map